import { invoke } from "@tauri-apps/api/core";
import { fromMaybe, nothing } from "../../maybe";
import { trimmedMaybe } from "../../trimmedString";
import { joinProjectPath } from "../../runtime";
import type { ProjectFs } from "../fs/types";
import type {
  ApplyBundleResult,
  CloudBackupStatus,
  ProjectGit,
} from "../git/types";
import type { ProjectBackend } from "./types";

class TauriProjectFs implements ProjectFs {
  constructor(private readonly rootPath: string) {}

  private abs(relativePath: string): string {
    return joinProjectPath(this.rootPath, relativePath);
  }

  async readFile(relativePath: string): Promise<Uint8Array> {
    const text = await invoke<string>("read_project_file", {
      path: this.abs(relativePath),
    });
    return new TextEncoder().encode(text);
  }

  async readText(relativePath: string): Promise<string> {
    return invoke<string>("read_project_file", {
      path: this.abs(relativePath),
    });
  }

  async writeFile(
    relativePath: string,
    data: Uint8Array | string,
  ): Promise<void> {
    const content =
      typeof data === "string" ? data : new TextDecoder().decode(data);
    await invoke("write_file", { path: this.abs(relativePath), content });
  }

  async mkdir(relativePath: string): Promise<void> {
    void relativePath;
    throw new Error("mkdir is not supported on the desktop project filesystem");
  }

  async stat(relativePath: string): ReturnType<ProjectFs["stat"]> {
    try {
      await this.readText(relativePath);
      return { isFile: true, isDirectory: false, size: 0, mtimeMs: Date.now() };
    } catch {
      return fromMaybe(nothing);
    }
  }

  async readdir(_relativePath: string): Promise<string[]> {
    throw new Error(
      "readdir is not supported on the desktop project filesystem",
    );
  }

  async remove(_relativePath: string): Promise<void> {
    throw new Error(
      "remove is not supported on the desktop project filesystem",
    );
  }

  async exists(relativePath: string): Promise<boolean> {
    try {
      await this.readText(relativePath);
      return true;
    } catch {
      return false;
    }
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

class TauriProjectGit implements ProjectGit {
  constructor(private readonly rootPath: string) {}

  async headOid(): ReturnType<ProjectGit["headOid"]> {
    const raw: unknown = await invoke("git_head_oid", {
      rootPath: this.rootPath,
    });
    return fromMaybe(trimmedMaybe(typeof raw === "string" ? raw : ""));
  }

  async isContentDirty(): Promise<boolean> {
    const status = await this.cloudBackupStatus();
    return status.gitDirty;
  }

  async commitIfDirty(
    message: string,
  ): ReturnType<ProjectGit["commitIfDirty"]> {
    const raw: unknown = await invoke("commit_project_now", {
      rootPath: this.rootPath,
      message,
    });
    return fromMaybe(trimmedMaybe(typeof raw === "string" ? raw : ""));
  }

  async ensureRepoWithInitialCommit(
    message: string,
  ): ReturnType<ProjectGit["ensureRepoWithInitialCommit"]> {
    return this.commitIfDirty(message);
  }

  async materializeFromBundle(
    _packBytes: Uint8Array,
    _tipOid: string,
  ): Promise<string> {
    throw new Error("Use restore_project_from_bundle on desktop");
  }

  async applyCloudBundle(
    packBytes: Uint8Array,
    tipOid: string,
    resolution: Parameters<ProjectGit["applyCloudBundle"]>[2] = null,
  ): Promise<ApplyBundleResult> {
    return invoke<ApplyBundleResult>("apply_cloud_bundle", {
      rootPath: this.rootPath,
      bundleBase64: bytesToBase64(packBytes),
      gitHeadOid: tipOid,
      resolution,
    });
  }

  async cloudBackupStatus(
    lastUploadedOid: Parameters<ProjectGit["cloudBackupStatus"]>[0] = null,
  ): Promise<CloudBackupStatus> {
    const status = await invoke<CloudBackupStatus>("cloud_backup_status", {
      rootPath: this.rootPath,
    });
    const trimmedM = trimmedMaybe(
      typeof lastUploadedOid === "string" ? lastUploadedOid : "",
    );
    if (trimmedM.tag === "just") {
      return { ...status, lastUploadedOid: trimmedM.value };
    }
    return status;
  }
}

export function createDiskBackend(rootPath: string): ProjectBackend {
  return {
    kind: "disk",
    rootPath,
    fs: new TauriProjectFs(rootPath),
    git: new TauriProjectGit(rootPath),
  };
}
